import { useEffect, useState } from 'react';
import { communications, mailAccounts } from '../api/endpoints';
import type { AvailableUser, ChannelAccount, MailAccount } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import {
    Card, Empty, ErrorBox, Field, fmtDate, PageHeader,
} from '../components/ui';
import { useAsync } from '../hooks';

interface FormState {
    id: string;
    revision: number;
    name: string;
    protocol: 'imap' | 'pop3';
    host: string;
    port: string;
    use_ssl: boolean;
    login: string;
    password: string;
    mailbox: string;
    is_active: boolean;
    smtp_host: string;
    smtp_port: string;
    smtp_ssl: boolean;
    inbound_mode: 'replies_auto' | 'all_manual';
    members: string[];
}

const EMPTY_FORM: FormState = {
    id: '', revision: 0, name: '', protocol: 'imap', host: '', port: '', use_ssl: true,
    login: '', password: '', mailbox: 'INBOX', is_active: true,
    smtp_host: '', smtp_port: '', smtp_ssl: true,
    inbound_mode: 'replies_auto', members: [],
};

const WA_ROLE_OPTIONS = [
    { code: 'manager', label: 'Менеджер' },
    { code: 'supervisor', label: 'Супервизор' },
    { code: 'specialist', label: 'Технический специалист' },
    { code: 'accountant', label: 'Бухгалтер' },
    { code: 'dispatcher', label: 'Диспетчер' },
];

/** WhatsApp-каналы: Meta Cloud API и Evolution (мост на обычный номер). */
function WhatsAppCard() {
    const channels = useAsync<ChannelAccount[]>(
        () => communications.channelAccounts().catch(() => [] as ChannelAccount[]),
        []);
    const [form, setForm] = useState<{
        provider: 'evolution' | 'whatsapp_cloud';
        number: string; credentialsRef: string; roles: string[];
    } | null>(null);
    const [err, setErr] = useState<unknown>(null);
    const [busy, setBusy] = useState(false);
    const [qrInfo, setQrInfo] = useState<{
        instance?: string; state?: string; qr?: string;
    } | null>(null);

    const waChannels = (channels.data ?? []).filter((c) => c.type === 'whatsapp');

    const toggleRole = (code: string) => {
        if (!form) return;
        const next = new Set(form.roles);
        if (next.has(code)) next.delete(code); else next.add(code);
        setForm({ ...form, roles: [...next] });
    };

    const create = async () => {
        if (!form || !form.number || !form.credentialsRef) return;
        setBusy(true); setErr(null);
        try {
            await communications.createChannelAccount({
                type: 'whatsapp', provider: form.provider,
                mailbox_or_number: form.number,
                credentials_ref: form.credentialsRef,
                access_scope: { allowed_roles: form.roles },
            });
            setForm(null);
            await channels.reload();
        } catch (e) {
            setErr(e);
        } finally {
            setBusy(false);
        }
    };

    const remove = async (a: ChannelAccount) => {
        if (!window.confirm(`Удалить WhatsApp-канал ${a.mailbox_or_number}?`)) return;
        setErr(null);
        try {
            await communications.deleteChannelAccount(a.id, a.revision);
            await channels.reload();
        } catch (e) {
            setErr(e);
        }
    };

    const connect = async (a: ChannelAccount) => {
        setBusy(true); setErr(null);
        try {
            const res = await communications.connectChannel(a.id);
            if (res.ok === false) {
                setErr(new Error(res.error || 'Не удалось подключить инстанс.'));
            }
            setQrInfo({ instance: res.instance, state: res.state, qr: res.qr });
        } catch (e) {
            setErr(e);
        } finally {
            setBusy(false);
        }
    };

    const isEvo = (a: ChannelAccount) => a.provider === 'evolution';

    return (
        <Card title="WhatsApp-каналы"
              actions={<button className="btn primary" onClick={() => setForm({ provider: 'evolution', number: '', credentialsRef: 'WA_EVOLUTION', roles: [] })}>+ Канал</button>}>
            <ErrorBox error={err} />
            {waChannels.length === 0 && !channels.loading && (
                <Empty text="Каналы не подключены. Создайте канал: Evolution — обычный номер по QR, Meta Cloud — официальный API." />
            )}
            {waChannels.length > 0 && (
                <div className="table-wrap">
                    <table className="table">
                        <thead>
                            <tr><th>Канал</th><th>Провайдер</th><th>credentials_ref</th><th>Доступ (роли)</th><th></th></tr>
                        </thead>
                        <tbody>
                            {waChannels.map((a) => (
                                <tr key={a.id}>
                                    <td><strong>{a.mailbox_or_number || '—'}</strong></td>
                                    <td className="muted">
                                        {isEvo(a) ? 'Evolution (Baileys)' : a.provider || '—'}
                                    </td>
                                    <td className="muted">{a.credentials_ref || '—'}</td>
                                    <td className="muted">
                                        {((a.access_scope?.allowed_roles ?? []) as string[]).join(', ') || '—'}
                                    </td>
                                    <td>
                                        {isEvo(a) && (
                                            <>
                                                <button className="btn small" disabled={busy}
                                                        onClick={() => void connect(a)}>
                                                    Подключить / QR
                                                </button>{' '}
                                            </>
                                        )}
                                        <button className="btn small danger" disabled={busy}
                                                onClick={() => void remove(a)}>Удалить</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {qrInfo && (
                <div className="modal-overlay" onClick={() => setQrInfo(null)}
                     role="dialog" aria-modal="true">
                    <div className="modal-card" onClick={(e) => e.stopPropagation()}
                         style={{ maxWidth: 420 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                            <strong style={{ flex: 1 }}>
                                Инстанс {qrInfo.instance || ''}: {qrInfo.state || 'unknown'}
                            </strong>
                            <button className="btn small" onClick={() => setQrInfo(null)}>✕</button>
                        </div>
                        {qrInfo.state === 'open' ? (
                            <div>Канал подключён — входящие приходят во «Входящие»,
                                 ответы уходят из переписки заявки.</div>
                        ) : qrInfo.qr ? (
                            <div>
                                <div className="muted" style={{ marginBottom: 8, fontSize: 13 }}>
                                    Отсканируйте в WhatsApp: Настройки → Связанные устройства →
                                    Привязка устройства. QR живёт ~30 с — при истечении нажмите
                                    «Подключить / QR» ещё раз.
                                </div>
                                <img src={qrInfo.qr} alt="QR-код WhatsApp"
                                     style={{ width: '100%', maxWidth: 320, display: 'block', margin: '0 auto' }} />
                            </div>
                        ) : (
                            <div className="muted">
                                QR не получен. Проверьте, что контейнер evolution запущен и
                                api_url/api_key в credentials верны; повторите через минуту.
                            </div>
                        )}
                    </div>
                </div>
            )}
            {form && (
                <div className="inline-form" style={{ marginTop: 8 }}>
                    <Field label="Провайдер">
                        <select value={form.provider}
                                onChange={(e) => setForm({
                                    ...form,
                                    provider: e.target.value as 'evolution' | 'whatsapp_cloud',
                                })}>
                            <option value="evolution">Evolution API — обычный номер по QR</option>
                            <option value="whatsapp_cloud">Meta Cloud API — официальный</option>
                        </select>
                    </Field>
                    <Field label={form.provider === 'evolution' ? 'Имя инстанса' : 'phone_number_id'}
                           hint={form.provider === 'evolution'
                               ? 'например, regway — так же назовите в credentials'
                               : 'идентификатор номера из Meta for Developers'}>
                        <input value={form.number}
                               placeholder={form.provider === 'evolution' ? 'regway' : '123456789012345'}
                               onChange={(e) => setForm({ ...form, number: e.target.value })} />
                    </Field>
                    <Field label="credentials_ref"
                           hint={form.provider === 'evolution'
                               ? 'имя env-переменной: JSON {api_url, api_key, instance, webhook_secret}'
                               : 'имя env-переменной: JSON {access_token, phone_number_id, app_secret, verify_token}'}>
                        <input value={form.credentialsRef}
                               onChange={(e) => setForm({ ...form, credentialsRef: e.target.value })} />
                    </Field>
                    <Field label="Видят входящие" hint="админ и супервизор видят канал всегда">
                        <span>
                            {WA_ROLE_OPTIONS.map((r) => (
                                <label key={r.code} style={{ marginRight: 12 }}>
                                    <input type="checkbox" checked={form.roles.includes(r.code)}
                                           onChange={() => toggleRole(r.code)} /> {r.label}
                                </label>
                            ))}
                        </span>
                    </Field>
                    <div className="toolbar">
                        <button className="btn primary" disabled={busy || !form.number || !form.credentialsRef}
                                onClick={() => void create()}>Создать</button>
                        <button className="btn" onClick={() => setForm(null)}>Отмена</button>
                    </div>
                </div>
            )}
            <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
                Evolution — самохостный мост (Baileys): входящие через webhook
                <code>/api/v1/integrations/webhook/evolution/&lt;secret&gt;/</code>,
                подключение — QR-кнопкой в строке канала. Meta Cloud webhook:
                <code>/api/v1/integrations/webhook/whatsapp/</code>.
            </div>
        </Card>
    );
}

export function MailAccountsPage() {
    const { user } = useAuth();
    const list = useAsync<MailAccount[]>(() => mailAccounts.list(), []);
    const users = useAsync<AvailableUser[]>(() => mailAccounts.availableUsers(), []);
    const [form, setForm] = useState<FormState | null>(null);
    const [err, setErr] = useState<unknown>(null);
    const [notice, setNotice] = useState('');
    const [busy, setBusy] = useState(false);

    const openNew = () => { setErr(null); setForm({ ...EMPTY_FORM }); };
    const openEdit = (a: MailAccount) => {
        setErr(null);
        setForm({
            id: a.id, revision: a.revision ?? 0,
            name: a.name ?? '', protocol: (a.protocol as 'imap' | 'pop3') || 'imap',
            host: a.host ?? '', port: a.port ? String(a.port) : '', use_ssl: a.use_ssl !== false,
            login: a.login ?? '', password: '', mailbox: a.mailbox || 'INBOX',
            is_active: a.is_active !== false,
            smtp_host: a.smtp_host ?? '', smtp_port: a.smtp_port ? String(a.smtp_port) : '',
            smtp_ssl: a.smtp_ssl !== false, members: a.members ?? [],
            inbound_mode: (a.inbound_mode as 'replies_auto' | 'all_manual') || 'replies_auto',
        });
    };

    const isOwner = (a: MailAccount) =>
        a.owner_username === user?.username || Boolean(user?.is_superuser);

    const save = async () => {
        if (!form) return;
        setBusy(true); setErr(null);
        const body: Record<string, unknown> = {
            name: form.name, protocol: form.protocol, host: form.host,
            login: form.login, use_ssl: form.use_ssl, mailbox: form.mailbox,
            is_active: form.is_active, members: form.members,
            smtp_host: form.smtp_host, smtp_ssl: form.smtp_ssl,
            inbound_mode: form.inbound_mode,
        };
        if (form.port.trim()) body.port = Number(form.port);
        if (form.smtp_port.trim()) body.smtp_port = Number(form.smtp_port);
        if (form.password) body.password = form.password;
        if (form.id && form.revision) body.revision = form.revision;
        try {
            if (form.id) await mailAccounts.update(form.id, body);
            else await mailAccounts.create(body);
            setForm(null);
            await list.reload();
        } catch (x) { setErr(x); } finally { setBusy(false); }
    };

    const act = async (fn: () => Promise<{ ok?: boolean; error?: string; started?: boolean }>,
                       okText: (r: { started?: boolean }) => string) => {
        setBusy(true); setErr(null); setNotice('');
        try {
            const r = await fn();
            setNotice(r.ok === false ? `Ошибка: ${r.error ?? 'неизвестно'}` : okText(r));
            await list.reload();
        } catch (x) { setErr(x); } finally { setBusy(false); }
    };

    // Пока хоть один ящик в очереди/синхронизируется — список обновляется
    // каждые 4 с: видны «В очереди» → «Получаем письма…» → итог/ошибка.
    const syncing = (list.data ?? []).some(
        (a) => a.sync_state === 'queued' || a.sync_state === 'running');
    useEffect(() => {
        if (!syncing) return;
        const t = setInterval(() => { void list.reload(); }, 4000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [syncing]);

    const remove = async (a: MailAccount) => {
        if (!window.confirm(`Удалить ящик «${a.display_name}»? Письма во «Входящих» сохранятся.`)) return;
        setBusy(true); setErr(null);
        try { await mailAccounts.remove(a.id); await list.reload(); }
        catch (x) { setErr(x); } finally { setBusy(false); }
    };

    // Три разных результата: попытка ≠ успех; «в очереди» ≠ «получено».
    const syncCell = (a: MailAccount) => {
        const staleMs = 150_000;
        const state = a.sync_state ?? 'idle';
        const runningStale = state === 'running'
            && a.updated_at != null
            && Date.now() - Date.parse(a.updated_at) > staleMs;
        const res = a.last_result;
        return (
            <div>
                {state === 'queued' && <span className="badge info">В очереди</span>}
                {state === 'running' && (
                    <span className="badge info">
                        {runningStale ? 'Синхронизация задерживается…' : 'Получаем письма…'}
                    </span>
                )}
                {a.last_success_at && (
                    <div className="muted">Успешно: {fmtDate(a.last_success_at)}</div>
                )}
                {a.last_polled_at && a.last_polled_at !== a.last_success_at && (
                    <div className="muted">Попытка: {fmtDate(a.last_polled_at)}</div>
                )}
                {!a.last_polled_at && !a.last_success_at && !a.last_error && (
                    <div className="muted">ещё не опрашивался</div>
                )}
                {res && res.fetched != null && (
                    <div className="muted">
                        новых {res.new ?? 0} · повторов {res.duplicates ?? 0} · загружено {res.fetched}
                    </div>
                )}
                {a.last_error && (
                    <div className="error-text" title={a.last_error}>
                        {a.last_error.slice(0, 80)}
                    </div>
                )}
            </div>
        );
    };

    const toggleMember = (id: string) => {
        if (!form) return;
        setForm({
            ...form,
            members: form.members.includes(id)
                ? form.members.filter((m) => m !== id)
                : [...form.members, id],
        });
    };

    return (
        <div>
            <PageHeader title="Почтовые ящики">
                <button className="btn primary" onClick={openNew}>+ Ящик</button>
            </PageHeader>
            <ErrorBox error={err} />
            {notice && <div className="alert alert-info" style={{ marginBottom: 8 }}>{notice}</div>}

            <Card title="Мои и доступные мне ящики">
                <ErrorBox error={list.error} />
                {(list.data ?? []).length === 0 && !list.loading && (
                    <Empty text="Ящики не подключены. Добавьте IMAP/POP3 — письма будут попадать во «Входящие»." />
                )}
                {(list.data ?? []).length > 0 && (
                    <div className="table-wrap">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Ящик</th><th>Протокол</th><th>Сервер</th><th>Папка</th>
                                    <th>Владелец</th><th>Доступ</th><th>Опрос</th><th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {(list.data ?? []).map((a) => (
                                    <tr key={a.id}>
                                        <td>
                                            <strong>{a.display_name}</strong>
                                            {a.is_active === false && <span className="muted"> · выкл</span>}
                                        </td>
                                        <td>{(a.protocol ?? 'imap').toUpperCase()}{a.use_ssl ? ' SSL' : ''}</td>
                                        <td className="muted">{a.host}{a.port ? `:${a.port}` : ''}</td>
                                        <td>{a.mailbox || 'INBOX'}</td>
                                        <td className="muted">{a.owner_username}</td>
                                        <td className="muted">
                                            {(a.member_usernames ?? []).length > 0
                                                ? a.member_usernames!.join(', ')
                                                : '—'}
                                        </td>
                                        <td className="muted">{syncCell(a)}</td>
                                        <td>
                                            <button className="btn small" disabled={busy}
                                                    onClick={() => void act(() => mailAccounts.testConnection(a.id),
                                                        () => 'Подключение проверено. Папка доступна.')}>
                                                Проверить
                                            </button>{' '}
                                            <button className="btn small" disabled={busy}
                                                    onClick={() => void act(() => mailAccounts.pollNow(a.id),
                                                        () => 'Синхронизация поставлена в очередь — результат появится в колонке «Опрос».')}>
                                                Опросить
                                            </button>{' '}
                                            {isOwner(a) && (
                                                <>
                                                    <button className="btn small" onClick={() => openEdit(a)}>Изменить</button>{' '}
                                                    <button className="btn small danger" disabled={busy}
                                                            onClick={() => void remove(a)}>Удалить</button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
                    При первом опросе импортируются письма за последние 30 дней (новые — первыми, до 100 за проход);
                    далее — только новые. Письма на сервере не удаляются и не помечаются прочитанными.
                    Автоопрос — каждые 2 минуты, пока ящик включён.
                </div>
            </Card>

            <WhatsAppCard />

            {form && (
                <Card title={form.id ? 'Редактировать ящик' : 'Новый ящик'}>
                    <div className="form-row">
                        <Field label="Название (необязательно)">
                            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                   placeholder="рабочий ящик" />
                        </Field>
                        <Field label="Протокол">
                            <select value={form.protocol}
                                    onChange={(e) => setForm({ ...form, protocol: e.target.value as 'imap' | 'pop3' })}>
                                <option value="imap">IMAP (рекомендуется)</option>
                                <option value="pop3">POP3</option>
                            </select>
                        </Field>
                        <Field label="Сервер">
                            <input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })}
                                   placeholder="imap.yandex.ru" />
                        </Field>
                        <Field label="Порт" hint="пусто — стандартный (993/995 SSL, 143/110 без)">
                            <input value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })}
                                   placeholder="" />
                        </Field>
                    </div>
                    <div className="form-row">
                        <Field label="Логин">
                            <input value={form.login} onChange={(e) => setForm({ ...form, login: e.target.value })}
                                   placeholder="me@box.ru" />
                        </Field>
                        <Field label={form.id ? 'Пароль (пусто — оставить прежний)' : 'Пароль'}
                               hint="пароль приложения, хранится зашифрованным">
                            <input type="password" value={form.password}
                                   onChange={(e) => setForm({ ...form, password: e.target.value })} />
                        </Field>
                        {form.protocol === 'imap' && (
                            <Field label="Папка">
                                <input value={form.mailbox}
                                       onChange={(e) => setForm({ ...form, mailbox: e.target.value })} />
                            </Field>
                        )}
                        <Field label=" ">
                            <label>
                                <input type="checkbox" checked={form.use_ssl}
                                       onChange={(e) => setForm({ ...form, use_ssl: e.target.checked })} />
                                {' '}SSL/TLS
                            </label>
                            <label style={{ marginLeft: 12 }}>
                                <input type="checkbox" checked={form.is_active}
                                       onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                                {' '}опрашивать
                            </label>
                        </Field>
                    </div>
                    {(users.data ?? []).length > 0 && (
                        <div className="form-row">
                            <Field label="Доступ другим пользователям" hint="общий ящик: общий курсор и лента входящих">
                                <div>
                                    {(users.data ?? [])
                                        .filter((u) => u.username !== user?.username)
                                        .map((u) => (
                                            <label key={u.id} style={{ marginRight: 12 }}>
                                                <input type="checkbox"
                                                       checked={form.members.includes(u.id)}
                                                       onChange={() => toggleMember(u.id)} />
                                                {' '}{u.username}
                                            </label>
                                        ))}
                                </div>
                            </Field>
                        </div>
                    )}
                    <div className="form-row">
                        <Field label="SMTP-сервер (исходящая)"
                               hint="пусто — выводится из входящего (imap. → smtp.)">
                            <input value={form.smtp_host}
                                   onChange={(e) => setForm({ ...form, smtp_host: e.target.value })}
                                   placeholder="smtp.yandex.ru" />
                        </Field>
                        <Field label="SMTP-порт" hint="пусто — 465 SSL или 587 STARTTLS">
                            <input value={form.smtp_port}
                                   onChange={(e) => setForm({ ...form, smtp_port: e.target.value })} />
                        </Field>
                        <Field label=" ">
                            <label>
                                <input type="checkbox" checked={form.smtp_ssl}
                                       onChange={(e) => setForm({ ...form, smtp_ssl: e.target.checked })} />
                                {' '}SMTP SSL (снять — STARTTLS)
                            </label>
                        </Field>
                    </div>
                    <div className="form-row">
                        <Field label="Обработка входящих">
                            <select value={form.inbound_mode}
                                    onChange={(e) => setForm({ ...form, inbound_mode: e.target.value as 'replies_auto' | 'all_manual' })}>
                                <option value="replies_auto">Ответы — автоматически, новые письма — вручную</option>
                                <option value="all_manual">Все письма — на ручной разбор</option>
                            </select>
                        </Field>
                    </div>
                    <div className="form-row">
                        <button className="btn primary" disabled={busy || !form.host || !form.login || (!form.id && !form.password)}
                                onClick={() => void save()}>
                            {busy ? 'Сохранение…' : 'Сохранить'}
                        </button>
                        <button className="btn" onClick={() => setForm(null)}>Отмена</button>
                    </div>
                </Card>
            )}
        </div>
    );
}
